import React from 'react';

import { reverse } from 'named-urls';

import { useMatch } from 'react-router-dom';

import routes from 'core/routes';

import { VatixError } from 'components/Error/types';

import Grid from 'components/Grid';
import { ColumnDefinition } from 'components/Grid/types';

import { formatMoment } from 'utils/formatters/timezone';
import { FULL_DATETIME_COLUMN_MIN_WIDTH, FULL_DATETIME_FORMAT } from 'core/constants';
import { useStore } from 'utils/hooks/store';

import { colors } from 'theme/colors';

import BasicDateOperators from 'components/Grid/operators/Date/Date';

import PipelinesActionCell from './PipelinesActionCell';

export const pipelinesListBasicColumns = [
  {
    field: 'name',
    headerName: 'Name',
    flex: 2,
    renderCell: ({ row }: { row: { default: boolean; name: string } }) => (
      <p>
        {row.name} <span style={{ color: colors.text.disabled }}>{row.default ? '(Default)' : ''}</span>
      </p>
    ),
  },
  {
    field: 'updatedAt',
    headerName: 'Last Modified',
    filterOperators: BasicDateOperators(),
    minWidth: FULL_DATETIME_COLUMN_MIN_WIDTH,
    renderCell: ({ value }: { value: string | null }) => (
      <span data-testid="pipelines-updated-at">
        {value !== null ? formatMoment(value, FULL_DATETIME_FORMAT) : 'N/A'}
      </span>
    ),
    valueFormatter: (value: string | null) => formatMoment(value, FULL_DATETIME_FORMAT),
    flex: 1,
  },
] as unknown as ColumnDefinition[];

const PipelinesList: React.FunctionComponent<{
  onError: (error: VatixError) => void;
  onRefreshCallback: (callback: Function) => void;
}> = ({ onError, onRefreshCallback }): React.ReactElement => {
  const { routing } = useStore();
  const match = useMatch(routes.dashboard.settings.objects.details.pipelines.toString());

  const { moduleName } = match?.params as { moduleName: string };

  const onRowClick = (uuid: string): void => {
    const path = reverse(routes.dashboard.settings.objects.details.pipelines.toString(), {
      moduleName,
    });
    routing.push(`${path + uuid}/`);
  };

  return (
    <Grid
      onRefreshCallback={onRefreshCallback}
      entity="pipelines"
      onError={onError}
      onRowClick={(params) => onRowClick(params.row.uuid)}
      sortBy={{ field: 'lastModified', sort: 'desc' }}
      customNoRowsText="No pipelines have been created yet"
      dataURL={`/api/entities/${moduleName}/pipelines/`}
      showHeader={false}
      showQuickFilter={false}
      basicColumns={
        [
          ...pipelinesListBasicColumns,
          {
            field: 'actions',
            type: 'actions',
            headerName: '',
            maxWidth: 50,
            ariaLabel: 'actions_cell',
            renderCell: ({
              row,
              colDef,
            }: {
              row: { uuid: string; name: string; lastModified: string; default: boolean };
              colDef: ColumnDefinition;
            }) => (
              <PipelinesActionCell
                rowData={{ uuid: row.uuid, name: row.name, isDefault: row.default }}
                redirect={onRowClick}
                actions={colDef.actions}
              />
            ),
            filterOperators: null,
            sortable: false,
          },
        ] as unknown as ColumnDefinition[]
      }
    />
  );
};

export default PipelinesList;
